import React, { useCallback, useState } from "react";

import { useMutation, useQuery } from "@apollo/client";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Box, Button, Theme } from "@mui/material";
import { nanoid } from "@reduxjs/toolkit";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { withStyles } from "tss-react/mui";

import { graphql } from "@/gql";
import {
  AllergiesKind,
  CreateRecipeInput,
  Recipe,
  UpdateRecipeInput,
} from "@/gql/graphql";
import useGlobalSnackbar from "@/hooks/useGlobalSnackbar";
import { displayError } from "@/utils/errors";

import { RecipeForm, RecipeFormContent, RecipeFormStepper } from "./components";
import CancelDuplicateRecipeModal from "./components/CancelDuplicateRecipeModal";
import { deleteRecipeMutation, recipesPageQuery } from "../Index/RecipesPage";
import { RecipeFormValues } from "../New/components/RecipeForm";

const Root = withStyles(Box, (theme: Theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    position: "relative",
    flex: 1,
    padding: theme.spacing(2),
    height: "100%",
  },
}));

const editRecipePageQuery = graphql(`
  query EditRecipePage($id: ID!, $allergies: [AllergiesKind!]) {
    recipe(id: $id) {
      ...RecipeForm
    }
  }
`);

const updateRecipeMutation = graphql(`
  mutation UpdateRecipe($input: UpdateRecipeInput!) {
    updateRecipe(input: $input) {
      recipe {
        ...RecipeListItem
      }
    }
  }
`);

const EditRecipePage: React.FC = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const mealPlanUrlPath = `/clients/${state?.clientId}/programs/${state?.programId}/edit`;
  const { recipeId } =
    useParams<{
      recipeId: Recipe["id"];
      programId: string;
      clientId: string;
    }>();
  const urlParams = new URLSearchParams(window.location.search);
  const queryParams = urlParams.get("allergies") ?? "";
  const queryType = urlParams.get("type") ?? "";
  const allergies = queryParams.length
    ? (queryParams.split(",") as AllergiesKind[])
    : [];
  const { data, loading } = useQuery(editRecipePageQuery, {
    variables: { id: recipeId ?? "", allergies },
  });
  const withAllergies = allergies.length !== 0;
  const mode = withAllergies ? "meal plan" : "recipes";
  const [updateRecipe] = useMutation(updateRecipeMutation);
  const [deleteRecipe] = useMutation(deleteRecipeMutation);

  const [cancelDuplicateModalVisibility, setcancelDuplicateModalVisibility] =
    useState<boolean>(false);
  const snackbar = useGlobalSnackbar();

  const handleDeleteRecipe = async (id: Recipe["id"]): Promise<void> => {
    try {
      const result = await deleteRecipe({
        variables: { input: { id: id } },
        refetchQueries: [recipesPageQuery],
      });
      if (result?.data?.deleteRecipe?.recipe?.id) {
        snackbar.open("Cancelled duplicate recipe.");
      }
      setcancelDuplicateModalVisibility(false);
      goBackToMealPlan();
    } catch (e) {
      snackbar.open(
        displayError(
          e,
          "Something went wrong while cancelling the duplication of your recipe. Please try again later."
        )
      );
    }
  };

  const saveRecipe = useCallback(
    async (input: CreateRecipeInput) => {
      const updateInput: UpdateRecipeInput = {
        id: recipeId ?? "",
        patch: input.attributes,
      };
      try {
        const result = await updateRecipe({
          variables: { input: updateInput },
        });
        if (result.data && queryType != "duplicate")
          snackbar.open("Recipe updated!");
      } catch (e) {
        snackbar.open(
          displayError(
            e,
            "Something went wrong while updating recipe. Please try again later."
          )
        );
      }
    },
    [updateRecipe]
  );

  const handleCancel = useCallback(() => {
    withAllergies ? setcancelDuplicateModalVisibility(true) : goBackToRecipes();
  }, []);

  function goBackToRecipes() {
    navigate("/database/recipes");
  }

  function goBackToMealPlan() {
    navigate(mealPlanUrlPath, {
      state: { fromDuplicateRecipe: true, step: state?.step },
    });
  }

  function handleRedirect() {
    withAllergies ? goBackToMealPlan() : goBackToRecipes();
  }

  const initialValues: RecipeFormValues = {
    name: data?.recipe?.name ?? "",
    preparationMinutes: data?.recipe?.preparationMinutes ?? 0,
    portions: data?.recipe?.numberOfPortions ?? 0,
    description: data?.recipe?.description ?? "",
    ingredients:
      data?.recipe?.ingredients?.map((ingredient) => ({
        id: ingredient.id,
        food: ingredient.food,
        foodPortion: ingredient.foodPortion,
        amount: ingredient.amount,
        _destroy: false,
      })) ?? [],
    instructions:
      data?.recipe?.instructions?.map((instruction) => ({
        id: nanoid(),
        text: instruction,
      })) ?? [],
    selectedIngredient: null,
    meta: {
      activeStep: 0,
      completedSteps: {
        0: false,
      },
    },
  };

  if (loading) return null;

  return (
    <RecipeForm
      initialValues={initialValues}
      onSubmit={saveRecipe}
      afterSubmit={handleRedirect}
    >
      {() => (
        <Root>
          <Box>
            <Button
              startIcon={<ArrowBackIcon fontSize="small" color="primary" />}
              variant="text"
              onClick={handleRedirect}
            >
              {`Back to ${mode}`}
            </Button>
          </Box>
          <Box
            position="absolute"
            top={47}
            display="flex"
            justifyContent="center"
            width="100%"
            zIndex={10}
          >
            <RecipeFormStepper />
          </Box>
          <RecipeFormContent
            title={mode === "meal plan" ? "Duplicate Recipe" : "Edit recipe"}
            submitLabel="Save"
            onCancel={handleCancel}
          />
          <CancelDuplicateRecipeModal
            recipeId={recipeId ?? ""}
            visibility={cancelDuplicateModalVisibility}
            onClose={() => setcancelDuplicateModalVisibility(false)}
            handleCancel={handleDeleteRecipe}
          />
        </Root>
      )}
    </RecipeForm>
  );
};

export default EditRecipePage;
